const DECIMAL_FORMAT: Intl.NumberFormatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
    useGrouping: true,
};
export function formatDecimalPlace(value: number | null | undefined): string {
    return formatNumber(value, DECIMAL_FORMAT);
}

const STANDARD_DECIMAL = ".";
const STANDARD_MINUS_SIGN = "-";
/**
 * locale-sensitive number parsing.
 * Invalid chars are ignored and stripped.
 */
export function parseNumber(value: string | number, locale: string): number {
    if (typeof value === "number") return value;
    const parts = Intl.NumberFormat(locale).formatToParts(-1.23);
    const localeDecimal =
        parts.find((part) => part.type === "decimal")?.value ?? STANDARD_DECIMAL;
    const localeMinusSign =
        parts.find((part) => part.type === "minusSign")?.value ??
        STANDARD_MINUS_SIGN;
    const invalidCharRegExp = new RegExp(
        String.raw`[^\d${localeDecimal}${localeMinusSign}]`,
        "g",
    );
    return Number.parseFloat(
        value
            .replaceAll(invalidCharRegExp, "")
            .replaceAll(localeDecimal, STANDARD_DECIMAL)
            .replaceAll(localeMinusSign, STANDARD_MINUS_SIGN),
    );
}

const FORMAT_REG_EXP =
    /^(?<useGrouping>0,)?0(?:.(?<requiredFractionDigits>0*)(?:\[(?<optionalFractionDigits>0+)\])?)?$/;
/**
 * Parses a `numeral`-style number format string into Intl.NumberFormatOptions
 * @param format `numeral`-style format, eg 0 0,0 0.00 0.00[000]
 * @returns Intl.NumberFormatOptions
 */
function parseNumberFormat(format: string): Intl.NumberFormatOptions {
    const match = format.match(FORMAT_REG_EXP);
    const useGrouping = match?.groups.useGrouping != null;
    const requiredFractionDigits =
        match?.groups.requiredFractionDigits?.length ?? 0;
    const optionalFractionDigits =
        match?.groups.optionalFractionDigits?.length ?? 0;
    const minimumFractionDigits = requiredFractionDigits;
    const maximumFractionDigits = requiredFractionDigits + optionalFractionDigits;
    return { useGrouping, minimumFractionDigits, maximumFractionDigits };
}

export function formatNumber(
    value: number | null | undefined,
    precisionOrFormat: number | string | Intl.NumberFormatOptions = "0,0",
): string {
    if (value == null) return "";

    let options: Intl.NumberFormatOptions = undefined;
    if (typeof precisionOrFormat === "object") {
        options = precisionOrFormat;
    } else if (typeof precisionOrFormat === "string") {
        options = parseNumberFormat(precisionOrFormat);
    } else {
        options = {
            minimumFractionDigits: 0,
            maximumFractionDigits: precisionOrFormat,
            useGrouping: false,
        };
    }

    // value is typed as `number`, but we have a lot `any`s and the previous library (numeral) could handle strings
    // let's handle them to be safe
    if (typeof value === "string") {
        value = parseNumber(value, "en-US");
    }

    return Intl.NumberFormat("en-US", options).format(value);
}

export function formatQuantity(
    value: number | null | undefined,
    precisionOrFormat: number | string = "0,0.[0000]",
) {
    return typeof precisionOrFormat === "number"
        ? formatNumber(value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: precisionOrFormat,
            useGrouping: true,
        })
        : formatNumber(value, precisionOrFormat);
}

export function formatPricingValue(value: number | null | undefined): string {
    return value == null ? "--" : formatDecimalPlace(value);
}
